import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { messagesApi } from "@/lib/api";

export default function MessageMembersDialog({ open, onOpenChange, projectId, projectName }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [includeClients, setIncludeClients] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setSubject(`Update — ${projectName || ""}`);
      setMessage("");
      setIncludeClients(true);
    }
  }, [open, projectName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSubmitting(true);
    try {
      const res = await messagesApi.send(projectId, {
        subject: subject.trim() || `Update — ${projectName || ""}`,
        message: message.trim(),
        include_clients: includeClients,
      });
      const sentCount = (res?.sent || []).length;
      const errCount = (res?.errors || []).length;
      if (errCount > 0) {
        toast.error(
          `${sentCount} sent, ${errCount} failed: ${res.errors[0]?.error || "unknown"}`
        );
      } else {
        toast.success(`Message sent to ${sentCount} recipient${sentCount === 1 ? "" : "s"}`);
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light">
            Message project members
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Sends an email to everyone invited to this project. Replies go to your inbox.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500">Subject</Label>
            <Input
              data-testid="message-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500">Message</Label>
            <Textarea
              data-testid="message-body"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Heads up — call time pushed to 8am. Park behind the building this time."
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 min-h-[140px]"
              required
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-zinc-300">
            <input
              type="checkbox"
              checked={includeClients}
              onChange={(e) => setIncludeClients(e.target.checked)}
              data-testid="message-include-clients"
              className="accent-amber-500"
            />
            Also send to client team members on the call sheet
          </label>
          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || !message.trim()}
              data-testid="message-send-btn"
              className="bg-amber-500 text-black hover:bg-amber-400 rounded-sm font-semibold"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending…</>
              ) : (
                <><Mail className="w-4 h-4 mr-1" /> Send Message</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
